import { useEffect, useState } from 'react';
import { AppLayout } from '../layouts/AppLayout';
import { Card, Button, Select } from '../components/ui';
import { AssetGallery } from '../components/assets/AssetGallery';
import { AssetUploadModal } from '../components/assets/AssetUploadModal';
import { useAssets, Asset, AssetTypeFilter } from '../hooks/useAssets';
import { Upload } from 'lucide-react';
import { getAssetPreviewImageUrl } from '../lib/assetPreview';

const ASSET_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
  { value: 'audio', label: 'Audio' },
];

function getFilteredEmptyState(typeFilter: AssetTypeFilter): { title: string; description: string } {
  if (typeFilter === 'image') {
    return { title: 'No image assets found', description: 'Upload image assets to get started.' };
  }
  if (typeFilter === 'video') {
    return { title: 'No video assets found', description: 'Upload video assets to get started.' };
  }
  if (typeFilter === 'audio') {
    return { title: 'No audio assets found', description: 'Upload audio assets to get started.' };
  }
  return { title: 'No assets yet', description: 'Upload images, videos, or audio to get started.' };
}

function AssetsPage() {
  const [selectedType, setSelectedType] = useState<AssetTypeFilter>('all');
  const { assets, loading, loadingMore, hasMore, totalCount, uploadAsset, loadMore } = useAssets(selectedType);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const emptyState = getFilteredEmptyState(selectedType);

  const handleUploadAssets = async (files: File[]) => {
    console.log(`[AssetsPage] bulk upload start — ${files.length} file(s)`);
    let successCount = 0;
    // Upload files sequentially so that a single slow or failing upload cannot
    // stall the entire batch or leave the UI in an infinite loading state.
    for (const file of files) {
      try {
        await uploadAsset(file, 'manual');
        successCount++;
        console.log(`[AssetsPage] file done (${successCount}/${files.length}) — "${file.name}"`);
      } catch (error) {
        console.error(`[AssetsPage] file failed — "${file.name}"`, error);
      }
    }
    console.log(`[AssetsPage] bulk upload complete — ${successCount}/${files.length} succeeded`);
    setShowUploadModal(false);
  };

  const handleAssetClick = (asset: Asset) => {
    setSelectedAsset(asset);
  };

  useEffect(() => {
    if (selectedAsset && !assets.some((asset) => asset.id === selectedAsset.id)) {
      setSelectedAsset(null);
    }
  }, [assets, selectedAsset]);

  // Right Sidebar - Asset Details
  const rightSidebarContent = selectedAsset && (
    <div className="space-y-4">
      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Asset Preview
        </h3>
        {selectedAsset.type === 'image' ? (
          <img
            src={selectedAsset.storageUrl}
            alt={selectedAsset.name}
            className="w-full rounded-md border border-border-subtle"
            loading="lazy"
            decoding="async"
          />
        ) : selectedAsset.type === 'video' ? (
          <video
            src={selectedAsset.storageUrl}
            controls
            className="w-full rounded-md border border-border-subtle"
            preload="none"
            poster={getAssetPreviewImageUrl(selectedAsset) ?? undefined}
          />
        ) : (
          <audio
            src={selectedAsset.storageUrl}
            controls
            className="w-full"
          />
        )}
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Asset Details
        </h3>
        <div className="space-y-2 text-xs text-text-secondary">
          <div className="flex justify-between">
            <span>Name:</span>
            <span className="text-text-primary font-medium truncate ml-2">
              {selectedAsset.name}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Type:</span>
            <span className="text-text-primary font-medium uppercase">
              {selectedAsset.type}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Size:</span>
            <span className="text-text-primary font-medium">
              {formatFileSize(selectedAsset.size)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Format:</span>
            <span className="text-text-primary font-medium">
              {selectedAsset.mimeType}
            </span>
          </div>
          {selectedAsset.width && selectedAsset.height && (
            <div className="flex justify-between">
              <span>Dimensions:</span>
              <span className="text-text-primary font-medium">
                {selectedAsset.width} × {selectedAsset.height}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Source:</span>
            <span className="text-text-primary font-medium capitalize">
              {selectedAsset.source}
            </span>
          </div>
          {selectedAsset.market && (
            <div className="flex justify-between">
              <span>Market:</span>
              <span className="text-text-primary font-medium capitalize">
                {selectedAsset.market}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Uploaded:</span>
            <span className="text-text-primary font-medium">
              {new Date(selectedAsset.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <AppLayout
      title="Asset Library"
      subtitle="Manage your images, videos, and audio"
      rightSidebar={rightSidebarContent}
      rightSidebarVisible={!!selectedAsset}
      actions={
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2"
        >
          <Upload className="w-4 h-4" />
          Upload Assets
        </Button>
      }
    >
      <div className="max-w-6xl mx-auto">
        <Card>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-text-primary">
              Type:
            </label>
            <Select
              value={selectedType}
              onChange={(value) => setSelectedType(value as AssetTypeFilter)}
              options={ASSET_TYPE_OPTIONS}
              className="w-44"
            />
          </div>
        </Card>
        <Card>
          <AssetGallery
            assets={assets}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            totalCount={totalCount}
            emptyTitle={emptyState.title}
            emptyDescription={emptyState.description}
            onLoadMore={loadMore}
            onAssetClick={handleAssetClick}
            onUploadClick={() => setShowUploadModal(true)}
          />
        </Card>
      </div>

      <AssetUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadAssets}
      />
    </AppLayout>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default AssetsPage;
